import {
  CounselorResourceService,
  type CounselorResource,
} from "@/services/counselor-resources";
import { ArrowLeft, BookOpen, Clock, FileText } from "lucide-react";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useLocation } from "wouter";
import "../styles/rich-content.css";
import { imageUrlCache } from "@/services/storage";

export const ResourceDetailPage: React.FC = () => {
  const [location, setLocation] = useLocation();

  // Extract ID from URL path like "/resources/9caaa78c-3a0a-4a98-93a8-2d89d99a8188"
  const id = location.split("/resources/")[1];
  const [resource, setResource] = useState<CounselorResource | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [displayContent, setDisplayContent] = useState<string | null>(null);

  // Load resource on component mount
  useEffect(() => {
    console.log("ResourceDetailPage - location:", location);
    console.log("ResourceDetailPage - extracted id:", id);
    if (id) {
      loadResource();
    }
  }, [id]);

  const loadResource = async () => {
    console.log("loadResource called with id:", id);
    setIsLoading(true);
    setError("");

    try {
      const response = await CounselorResourceService.getPublishedResource(id!);
      console.log("Resource loaded successfully:", response);
      setResource(response);
    } catch (err: any) {
      console.error("Failed to load counselor resource:", err);
      console.error("Error details:", {
        status: err.status,
        message: err.message,
        data: err.data,
      });

      if (err.status === 404) {
        setError(
          "Resource not found. It may have been unpublished or removed."
        );
      } else {
        setError(
          `Failed to load resource. Error: ${err.message || "Unknown error"}`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resolve image file IDs in content to downloadable URLs for read-only display
  useEffect(() => {
    const resolveImages = async () => {
      const html = resource?.content || "";
      if (!html) {
        setDisplayContent(null);
        return;
      }

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const imgs = Array.from(doc.querySelectorAll("img"));
        if (imgs.length === 0) {
          setDisplayContent(html);
          return;
        }

        await Promise.all(
          imgs.map(async (img) => {
            const src = (img.getAttribute("src") || "").trim();
            if (!src) return;
            // Skip if data URL (already embedded)
            if (/^data:/i.test(src)) return;
            // Skip if already a full URL (http/https)
            if (/^https?:\/\//i.test(src)) return;
            // Treat any other src as a fileId (legacy content) and resolve to a signed URL
            const url = await imageUrlCache.getImageUrl(src);
            if (url) {
              img.setAttribute("src", url);
            }
          })
        );

        setDisplayContent(doc.body.innerHTML);
      } catch (e) {
        // Fallback to original content on any error
        setDisplayContent(resource?.content || null);
      }
    };

    resolveImages();
  }, [resource?.content]);

  const goBack = () => {
    setLocation("/resources");
  };

  // Read-only Quill configuration (no toolbar, read-only mode)
  const readOnlyModules = {
    toolbar: false, // Disable toolbar completely
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "color",
    "background",
    "align",
    "link",
    "image",
    "blockquote",
    "code-block",
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading resource...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <button
            onClick={goBack}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground border border-border rounded-md hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Resources</span>
          </button>
          <BookOpen className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Resource</h1>
            <p className="text-muted-foreground">Resource details</p>
          </div>
        </div>

        {/* Error Message */}
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-8">
          <div className="text-center">
            <FileText className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-medium text-destructive mb-2">
              Unable to Load Resource
            </h3>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={loadResource}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={goBack}
                className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors"
              >
                Back to Resources
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <button
            onClick={goBack}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground border border-border rounded-md hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Resources</span>
          </button>
          <BookOpen className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Resource</h1>
            <p className="text-muted-foreground">Resource not found</p>
          </div>
        </div>

        <div className="bg-background border border-border rounded-md p-8">
          <div className="text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Resource Not Found
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              The resource you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={goBack}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Back to Resources
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <button
          onClick={goBack}
          className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground border border-border rounded-md hover:bg-accent transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Resources</span>
        </button>
        <BookOpen className="h-8 w-8 text-primary" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground">
            {resource.title}
          </h1>
          <p className="text-muted-foreground">Resource details</p>
        </div>
      </div>

      {/* Resource Info */}
      <div className="bg-accent/50 border border-border rounded-md p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            {resource.description && (
              <p className="text-sm text-muted-foreground mb-2">
                {resource.description}
              </p>
            )}
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Clock className="h-3 w-3" />
                <span>
                  Last updated:{" "}
                  {new Date(resource.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resource Content */}
      {resource.content ? (
        <div className="bg-background border border-border rounded-md p-6">
          <div className="quill-readonly">
            <ReactQuill
              value={displayContent ?? resource.content}
              readOnly={true}
              theme="snow"
              modules={readOnlyModules}
              formats={formats}
            />
          </div>
        </div>
      ) : (
        <div className="bg-background border border-border rounded-md p-8">
          <div className="text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Resource content is empty
            </h3>
            <p className="text-sm text-muted-foreground">
              This resource document exists but has no content yet.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
